import { View, StyleSheet, Pressable, Linking, Image } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useModal } from "@/hooks/useModal";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ImagePreviewModal } from "@/components/screens/shared/FilesTab/components/ImagePreviewModal";

interface Attachment {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
  bucket: string;
  uploadedAt: string;
  uploadedBy: string;
}

interface FilesTabProps {
  attachments?: Attachment[];
}

export function FilesTab({ attachments }: FilesTabProps) {
  const { colors } = useTheme();
  const { openModal, closeModal } = useModal();

  if (!attachments || attachments.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <MaterialCommunityIcons name="paperclip" size={48} color={colors.placeholder} />
        <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>No files attached</ThemedText>
      </View>
    );
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImageFile = (type: string) => type.startsWith("image/");

  const getFileIcon = (type: string): keyof typeof MaterialCommunityIcons.glyphMap => {
    if (type.startsWith("image/")) return "file-image";
    if (type.startsWith("video/")) return "file-video";
    if (type.includes("pdf")) return "file-pdf-box";
    if (type.includes("word") || type.includes("document")) return "file-word";
    if (type.includes("excel") || type.includes("spreadsheet")) return "file-excel";
    return "file-document";
  };

  const handleOpenFile = (file: Attachment) => {
    if (isImageFile(file.type)) {
      openModal(
        <ImagePreviewModal
          url={file.url}
          name={file.name}
          onClose={closeModal}
          onOpenInNewTab={() => Linking.openURL(file.url)}
        />,
      );
    } else {
      Linking.openURL(file.url);
    }
  };

  return (
    <View style={styles.container}>
      {attachments.map((file) => (
        <Pressable
          key={file.id}
          onPress={() => handleOpenFile(file)}
          style={({ pressed }) => [
            styles.fileItem,
            { backgroundColor: colors.backgroundSecondary, borderColor: colors.border },
            pressed && styles.pressed,
          ]}
        >
          {isImageFile(file.type) ? (
            <Image source={{ uri: file.url }} style={styles.imageThumbnail} resizeMode="cover" />
          ) : (
            <MaterialCommunityIcons name={getFileIcon(file.type)} size={24} color={colors.primary} />
          )}
          <View style={styles.fileInfo}>
            <ThemedText style={styles.fileName} numberOfLines={1}>
              {file.name}
            </ThemedText>
            <ThemedText style={[styles.fileMeta, { color: colors.textSecondary }]}>
              {formatFileSize(file.size)}
            </ThemedText>
          </View>
          <MaterialCommunityIcons name="open-in-new" size={18} color={colors.textSecondary} />
        </Pressable>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    gap: 8,
  },
  emptyContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
  },
  fileItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  pressed: {
    opacity: 0.7,
  },
  fileInfo: {
    flex: 1,
    gap: 2,
  },
  fileName: {
    fontSize: 14,
    fontWeight: "500",
  },
  fileMeta: {
    fontSize: 12,
  },
  imageThumbnail: {
    width: 40,
    height: 40,
    borderRadius: 4,
  },
});
